/**
 * LiveActivityStreamPage — Operate · Live Surveillance.
 *
 * Real-time, newest-first feed of every send across both pillars. Polls
 * GET /api/admin/operate/live-feed?limit=50 every 5s (pausable) and renders a
 * live-updating table with status chips, kind badges, and source chips.
 *
 * Mirrors MeasureOverviewPage conventions: useEffect mount fetch, loading
 * skeleton, graceful error Alert, KPI cards, PillarPageShell wrapper.
 *
 * Data source:
 *   - /api/admin/operate/live-feed?limit=50
 *     → { events: [{ id, recipient_email, subject_line, status, at, kind,
 *                     template_name, source }] }
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Alert,
  Chip,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tooltip,
  Divider,
} from "@mui/material";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { useNavigate } from "react-router-dom";
import PillarPageShell from "../shared/PillarPageShell";
import { HelpTip } from "../../help";
import apiService from "../../../services/api";

const POLL_MS = 5000;
const LIMIT = 50;

const OPERATE_TEAL = "#146152";

interface LiveEvent {
  id: string | number;
  recipient_email: string | null;
  subject_line: string | null;
  status: string | null;
  at: string | null;
  kind: string | null;
  template_name: string | null;
  source: string | null;
}

/** Status → chip color + label. delivered/opened/clicked = greenish, bounced = red, sent = grey. */
const STATUS_STYLE: Record<
  string,
  { bg: string; fg: string; label: string }
> = {
  delivered: { bg: "rgba(20,97,82,0.12)", fg: "#146152", label: "Delivered" },
  opened: { bg: "rgba(20,97,82,0.12)", fg: "#0F7B5F", label: "Opened" },
  clicked: { bg: "rgba(20,97,82,0.12)", fg: "#0B6E4F", label: "Clicked" },
  sent: { bg: "rgba(0,0,0,0.06)", fg: "#5D6D7E", label: "Sent" },
  queued: { bg: "rgba(0,0,0,0.06)", fg: "#5D6D7E", label: "Queued" },
  processed: { bg: "rgba(0,0,0,0.06)", fg: "#5D6D7E", label: "Processed" },
  bounced: { bg: "rgba(192,57,43,0.12)", fg: "#C0392B", label: "Bounced" },
  dropped: { bg: "rgba(192,57,43,0.12)", fg: "#C0392B", label: "Dropped" },
  deferred: { bg: "rgba(212,172,13,0.15)", fg: "#9A7D0A", label: "Deferred" },
  failed: { bg: "rgba(192,57,43,0.12)", fg: "#C0392B", label: "Failed" },
  spam: { bg: "rgba(192,57,43,0.12)", fg: "#C0392B", label: "Spam Report" },
};

function statusStyle(status: string | null) {
  const key = (status ?? "").toLowerCase();
  return (
    STATUS_STYLE[key] ?? {
      bg: "rgba(0,0,0,0.06)",
      fg: "#5D6D7E",
      label: status ? status.charAt(0).toUpperCase() + status.slice(1) : "—",
    }
  );
}

/** Kind → slate (transactional) / gold (nurture/marketing) badge. */
function kindBadge(kind: string | null): {
  label: string;
  color: string;
  bg: string;
} {
  const k = (kind ?? "").toLowerCase();
  if (k === "transactional") {
    return { label: "T · Transactional", color: "#5D6D7E", bg: "rgba(93,109,126,0.12)" };
  }
  if (k === "nurture" || k === "marketing") {
    return { label: "N · Nurture", color: "#9A7D0A", bg: "rgba(212,172,13,0.15)" };
  }
  return { label: kind ?? "—", color: "#5D6D7E", bg: "rgba(0,0,0,0.06)" };
}

/** Source → trigger / campaign-flow chip styling. */
function sourceLabel(source: string | null): string {
  const s = (source ?? "").toLowerCase();
  if (s === "trigger") return "Trigger";
  if (s === "campaign-flow" || s === "campaign_flow" || s === "campaign") return "Campaign Flow";
  if (!source) return "—";
  return source.charAt(0).toUpperCase() + source.slice(1);
}

/** Relative-time formatter ("just now", "12s ago", "3m ago", "2h ago", "4d ago"). */
function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  if (diff < 0) return "just now";
  const sec = Math.floor(diff / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function absoluteTime(iso: string | null): string {
  if (!iso) return "Unknown time";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unknown time";
  return d.toLocaleString();
}

const LiveActivityStreamPage: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<LiveEvent[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  // Re-render trigger so relative timestamps stay fresh even while paused.
  const [, setTick] = useState(0);

  const mountedRef = useRef(true);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  // Monotonic request id. mountedRef only protects against unmount — with a 5s
  // poll interval a slow response could still land AFTER a newer one and paint
  // an older feed (and an older "last updated" stamp) over it.
  const reqSeq = useRef(0);

  const fetchFeed = useCallback(async (isInitial: boolean) => {
    const seq = ++reqSeq.current;
    const stale = () => !mountedRef.current || reqSeq.current !== seq;
    if (isInitial && mountedRef.current) setLoading(true);
    try {
      const res: any = await apiService.get(
        `/api/admin/operate/live-feed?limit=${LIMIT}`,
      );
      if (stale()) return;
      const payload = res?.data ?? res;
      const list: LiveEvent[] = Array.isArray(payload?.events)
        ? payload.events
        : Array.isArray(payload)
          ? payload
          : [];
      // Newest-first.
      const sorted = [...list].sort((a, b) => {
        const ta = a.at ? new Date(a.at).getTime() : 0;
        const tb = b.at ? new Date(b.at).getTime() : 0;
        return tb - ta;
      });
      setEvents(sorted);
      setError(null);
      setLastUpdated(new Date());
    } catch (e: any) {
      if (!stale()) setError(e?.message ?? "Failed to load live feed");
    } finally {
      if (isInitial && !stale()) setLoading(false);
    }
  }, []);

  // Initial fetch + 5s polling (skipped while paused).
  useEffect(() => {
    mountedRef.current = true;
    fetchFeed(true);
    const id = setInterval(() => {
      if (!pausedRef.current) fetchFeed(false);
    }, POLL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [fetchFeed]);

  // Keep relative timestamps fresh once per second.
  useEffect(() => {
    const id = setInterval(() => {
      if (mountedRef.current) setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // ── Derived KPIs ───────────────────────────────────────────────────────────
  const total = events?.length ?? null;
  const deliveredish =
    events?.filter((e) =>
      ["delivered", "opened", "clicked"].includes((e.status ?? "").toLowerCase()),
    ).length ?? null;
  const bounced =
    events?.filter((e) =>
      ["bounced", "dropped", "failed", "spam"].includes(
        (e.status ?? "").toLowerCase(),
      ),
    ).length ?? null;
  const transactional =
    events?.filter((e) => (e.kind ?? "").toLowerCase() === "transactional")
      .length ?? null;
  const nurture =
    events?.filter((e) =>
      ["nurture", "marketing"].includes((e.kind ?? "").toLowerCase()),
    ).length ?? null;

  const fmt = (n: number | null) => (n == null ? "—" : n.toLocaleString());

  // ── Live indicator (pulsing dot when live, static amber when paused) ────────
  const liveIndicator = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={{
          position: "relative",
          width: 10,
          height: 10,
          display: "inline-flex",
        }}
      >
        <Box
          component="span"
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            bgcolor: paused ? "#9A7D0A" : OPERATE_TEAL,
            ...(paused
              ? {}
              : {
                  animation: "ls-live-ping 1.6s cubic-bezier(0,0,0.2,1) infinite",
                  "@keyframes ls-live-ping": {
                    "0%": { transform: "scale(1)", opacity: 0.7 },
                    "80%, 100%": { transform: "scale(2.4)", opacity: 0 },
                  },
                }),
          }}
        />
        <Box
          component="span"
          sx={{
            position: "relative",
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: paused ? "#9A7D0A" : OPERATE_TEAL,
          }}
        />
      </Box>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          color: paused ? "#9A7D0A" : OPERATE_TEAL,
        }}
      >
        {paused ? "Paused" : "Live"}
      </Typography>
    </Box>
  );

  const rightActions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      {liveIndicator}
      <Button
        size="small"
        variant="outlined"
        startIcon={paused ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
        onClick={() => {
          const next = !paused;
          setPaused(next);
          if (!next) fetchFeed(false); // resume → fetch immediately
        }}
        sx={{
          color: OPERATE_TEAL,
          borderColor: OPERATE_TEAL,
          "&:hover": { borderColor: OPERATE_TEAL, bgcolor: "rgba(20,97,82,0.06)" },
        }}
      >
        {paused ? "Resume" : "Pause"}
      </Button>
      <Button
        size="small"
        variant="contained"
        endIcon={<OpenInNewRoundedIcon />}
        onClick={() => navigate("/dashboard/communications/operate/queue")}
        sx={{
          bgcolor: OPERATE_TEAL,
          "&:hover": { bgcolor: "#0F4E42" },
        }}
      >
        Manage
      </Button>
    </Box>
  );

  return (
    <PillarPageShell
      pillar="operate"
      title="Live Activity Stream"
      subtitle="Real-time feed of every send across both pillars · newest first."
      helpTipId="live.stream.title"
      rightActions={rightActions}
    >
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error} — polling will retry automatically.
        </Alert>
      )}

      {/* KPI cards (current window) */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {loading && !events ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={i}>
              <Skeleton variant="rectangular" height={104} sx={{ borderRadius: 2 }} />
            </Grid>
          ))
        ) : (
          <>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card sx={{ borderRadius: 2, borderLeft: `6px solid ${OPERATE_TEAL}` }}>
                <CardContent>
                  <Typography variant="overline" sx={{ color: "text.secondary" }}>
                    Events in window
                  </Typography>
                  <Typography variant="h5" sx={{ color: OPERATE_TEAL, mt: 0.5 }}>
                    {fmt(total)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    last {LIMIT}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="overline" sx={{ color: "text.secondary" }}>
                    Delivered+
                  </Typography>
                  <Typography variant="h5" sx={{ color: "#146152", mt: 0.5 }}>
                    {fmt(deliveredish)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    delivered · opened · clicked
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card
                sx={{
                  borderRadius: 2,
                  borderLeft: `6px solid ${bounced ? "#C0392B" : "rgba(0,0,0,0.06)"}`,
                }}
              >
                <CardContent>
                  <Typography variant="overline" sx={{ color: "text.secondary" }}>
                    Bounced
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ color: bounced ? "#C0392B" : "text.primary", mt: 0.5 }}
                  >
                    {fmt(bounced)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {bounced ? "needs attention" : "all clear"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="overline" sx={{ color: "text.secondary" }}>
                    Transactional
                  </Typography>
                  <Typography variant="h5" sx={{ color: "#5D6D7E", mt: 0.5 }}>
                    {fmt(transactional)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    must-deliver
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="overline" sx={{ color: "text.secondary" }}>
                    Nurture
                  </Typography>
                  <Typography variant="h5" sx={{ color: "#9A7D0A", mt: 0.5 }}>
                    {fmt(nurture)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    discretionary
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Live stream table */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              mb: 1,
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Activity feed <HelpTip id="live.kind_badge" size={12} />
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {lastUpdated
                ? `Updated ${relativeTime(lastUpdated.toISOString())}`
                : "—"}
              {paused && " · polling paused"}
            </Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />
        </CardContent>

        {loading && !events ? (
          <Box sx={{ px: 2, pb: 2 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={44} sx={{ mb: 1, borderRadius: 1 }} />
            ))}
          </Box>
        ) : events && events.length > 0 ? (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Recipient</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Kind</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((ev, idx) => {
                  const ss = statusStyle(ev.status);
                  const kb = kindBadge(ev.kind);
                  const isNewest = idx === 0;
                  return (
                    <TableRow
                      key={`${ev.id ?? idx}-${ev.at ?? ""}`}
                      hover
                      sx={{
                        ...(isNewest && !paused
                          ? { animation: "ls-row-in 0.6s ease" }
                          : {}),
                        "@keyframes ls-row-in": {
                          "0%": { backgroundColor: "rgba(20,97,82,0.10)" },
                          "100%": { backgroundColor: "transparent" },
                        },
                      }}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip title={absoluteTime(ev.at)} arrow placement="right">
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {relativeTime(ev.at)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 220,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ev.recipient_email || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={ev.template_name ? `Template: ${ev.template_name}` : ""}
                          arrow
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 320,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {ev.subject_line || (
                              <span style={{ color: "rgba(0,0,0,0.4)" }}>—</span>
                            )}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={ss.label}
                          sx={{
                            bgcolor: ss.bg,
                            color: ss.fg,
                            fontWeight: 600,
                            borderRadius: 1,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={kb.label}
                          sx={{
                            color: kb.color,
                            bgcolor: kb.bg,
                            borderColor: kb.color,
                            fontWeight: 600,
                            borderRadius: 1,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={sourceLabel(ev.source)}
                          sx={{ borderRadius: 1, color: "text.secondary" }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
            <Typography variant="h6" sx={{ color: "text.secondary", mb: 0.5 }}>
              No activity yet
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {paused
                ? "Polling is paused. Resume to watch sends arrive in real time."
                : "Waiting for the next send. New events will appear here automatically."}
            </Typography>
          </Box>
        )}
      </Card>
    </PillarPageShell>
  );
};

export default LiveActivityStreamPage;
